import { Tooltip } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiClassificationStatusEnum } from '../../enums/AiClassificationStatusEnum';
import { useTags } from '../../hooks/useTags';
import type File from '../../interfaces/File';
import { FileService } from '../../services/fileService';
import { Icon } from '../Core/Icon';
import type { iconComponents } from '../Core/IconImports';

interface FileClassificationStatusIconProps {
  file: File;
}

const fileService = new FileService();

const FileAiClassificationStatusIcon = ({ file }: FileClassificationStatusIconProps) => {
  const { t } = useTranslation();
  const [tooltipContent, setTooltipContent] = useState(t('common.clickForDetailAiClassification'));
  const [loading, setLoading] = useState(false);
  const { tags } = useTags();

  /**
   * Handles the click event for the classification status icon.
   * If the file is already classified, it will show the tags.
   * If the file is not classified, it will show the classification status.
   */
  const handleClick = async () => {
    if (loading || tooltipContent !== t('common.clickForDetailAiClassification')) return;

    setLoading(true);
    try {
      const response = await fileService.getFile(file.id);

      setTooltipContent(
        response.tags
          .map((tag) => {
            const t = tags.find((t) => t.id === tag.id);
            return `- ${t?.title}: ${tag.explanation}`;
          })
          .join('\n'),
      );
    } catch (error) {
      setTooltipContent(
        t('errors.genericFetchError', { resource: t('common.aiClassificationStatus') }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Returns the icon for the classification status.
   * @param status - The classification status.
   * @returns The icon for the classification status.
   */
  const getIconForStatus = (status: AiClassificationStatusEnum): keyof typeof iconComponents => {
    switch (status) {
      case AiClassificationStatusEnum.Classifying:
        return 'TagAiLoaderIcon';
      case AiClassificationStatusEnum.Done:
        return 'TagAICheckIcon';
      case AiClassificationStatusEnum.Error:
        return 'TagAiTriangleAlertIcon';
      default:
        return 'TagAiLoaderIcon';
    }
  };

  return (
    <Tooltip title={loading ? t('common.loading') : tooltipContent}>
      <div onClick={handleClick} className="cursor-pointer">
        <Icon name={getIconForStatus(file.aiOperationStatus)} className="text-zinc h-5 w-5" />
      </div>
    </Tooltip>
  );
};

export default FileAiClassificationStatusIcon;
